import { YEAR_END_MAX_DATE, YEAR_END_MIN_DATE } from '@/shared/constants';
import { booleanColumn } from '@/shared/grids/columns/booleanColumn';
import { buttonColumn } from '@/shared/grids/columns/buttonColumn';
import { dateColumn } from '@/shared/grids/columns/dateColumn';
import { hiddenColumn } from '@/shared/grids/columns/hiddenColumn';
import { idColumn } from '@/shared/grids/columns/idColumn';
import { notesExcerptColumn } from '@/shared/grids/columns/notesExcerptColumn';
import { notesIconColumn } from '@/shared/grids/columns/notesIconColumn';
import { registrationNumberColumn } from '@/shared/grids/columns/registrationNumberColumn';
import { textColumn } from '@/shared/grids/columns/textColumn';
import { ClientType, Priority, TaskStatus, VatSchedule } from '@/shared/types/enum';
import { GridSettings } from '@/shared/types/grid';
import { ScheduleDay } from '@/shared/types/schedule';
import { enumOptions } from '@/shared/utils/enum';
import { formatEnum } from '@/shared/utils/format';

import { TaskView } from '@prisma/client';

export const taskGrid: GridSettings<TaskView & { scheduleDaysDescription: string }> = {
  extraSelect: [
    'scheduleDay',
    'smartTaskId',
    'startable',
    'completable',
    'currentSubTaskIndex',
    'subTaskCount',
    'subTaskCompleteCount',
  ],
  adapter: (task) => {
    if (task.scheduleDay) {
      task.scheduleDaysDescription = formatEnum(ScheduleDay, task.scheduleDay);
    }
  },
  defaultFilters: {
    status: {
      operator: 'notequal',
      value: TaskStatus.Completed,
    },
  },
  defaultSort: {
    deadlineDate: 'asc',
  },
  extraSort: {
    clientName: 'asc',
    task: 'asc',
  },
  columns: [
    idColumn(),
    hiddenColumn({
      field: 'recordsStatus',
    }),
    hiddenColumn({
      field: 'dueTimeFrames',
    }),
    notesIconColumn({
      field: 'hasNotes',
    }),
    textColumn({
      header: 'Task',
      field: 'task',
      width: 300,
    }),
    textColumn({
      header: 'Service',
      field: 'serviceId',
      width: 150,
      extra: {
        displayField: 'serviceName',
        sortField: 'serviceName',
        filter: {
          lookup: 'Service',
        },
      },
    }),
    textColumn({
      header: 'Client Type',
      field: 'clientType',
      visible: false,
      extra: {
        displayField: 'clientTypeName',
        sortField: 'clientTypeName',
        filter: {
          options: enumOptions(ClientType),
        },
      },
    }),
    textColumn({
      header: 'Client Reference',
      field: 'clientInternalReference',
      visible: false,
    }),
    textColumn({
      header: 'Client',
      field: 'clientId',
      width: 300,
      extra: {
        displayField: 'clientName',
        sortField: 'clientName',
        filter: {
          lookup: 'ClientView',
        },
      },
    }),
    textColumn({
      header: 'First Name',
      field: 'clientFirstName',
      visible: false,
    }),
    textColumn({
      header: 'Last Name',
      field: 'clientLastName',
      visible: false,
    }),
    registrationNumberColumn({
      field: 'clientCompanyRegistrationNumber',
    }),
    textColumn({
      header: 'UTR',
      field: 'clientUtr',
      visible: false,
    }),
    dateColumn({
      header: 'Year End',
      field: 'clientYearEnd',
      format: 'dayLongMonth',
      visible: false,
      extra: {
        filter: {
          minDate: YEAR_END_MIN_DATE,
          maxDate: YEAR_END_MAX_DATE,
        },
      },
    }),
    textColumn({
      header: 'VAT Quarters',
      field: 'clientVatQuarters',
      visible: false,
      extra: {
        displayField: 'clientVatQuartersName',
        filter: {
          options: enumOptions(VatSchedule, '/'),
        },
      },
    }),
    booleanColumn({
      header: 'Records Required',
      field: 'recordsRequired',
      visible: false,
    }),
    buttonColumn({
      uid: 'recordsReceivedButton',
      header: 'Records Received',
      field: 'recordsReceivedDate',
      type: 'date',
      action: 'setDateToToday',
      iconClass: 'fa-solid fa-folder-closed',
      class: 'btn-primary',
      toggleIf: {
        field: 'recordsReceivedDate',
        value: null,
        negate: true,
      },
      hideIf: {
        field: 'recordsRequired',
        value: null,
      },
    }),
    dateColumn({
      header: 'Records Received On',
      field: 'recordsReceivedDate',
      emptyText: '${if(recordsRequired)}Not received${else}Not required${/if}',
      visible: false,
    }),
    dateColumn({
      header: 'Start',
      field: 'plannedStartDate',
      overrideDisplayField: 'startedDate',
      dueIf: {
        field: 'startable',
        value: true,
      },
    }),
    buttonColumn({
      uid: 'startedButton',
      header: 'Started',
      field: 'startedDate',
      type: 'date',
      action: 'setDateToToday',
      iconClass: 'fa-solid fa-play',
      class: 'btn-warning',
      toggleIf: {
        field: 'startedDate',
        value: null,
        negate: true,
      },
      hideIf: {
        field: 'completedDate',
        value: null,
        negate: true,
      },
    }),
    dateColumn({
      header: 'Started On',
      field: 'startedDate',
      emptyText: 'Not started',
      visible: false,
    }),
    textColumn({
      header: 'Started By',
      field: 'startedByUserId',
      visible: false,
      extra: {
        displayField: 'startedByUserName',
        sortField: 'startedByUserName',
        filter: {
          lookup: 'MemberView',
        },
      },
    }),
    dateColumn({
      header: 'Complete',
      field: 'plannedCompletionDate',
      dueIf: {
        field: 'completable',
        value: true,
      },
    }),
    buttonColumn({
      uid: 'completedButton',
      header: 'Completed',
      field: 'completedDate',
      type: 'date',
      action: 'setDateToToday',
      iconClass: 'fa-solid fa-check',
      class: 'btn-success',
      toggleIf: {
        field: 'completedDate',
        value: null,
        negate: true,
      },
    }),
    dateColumn({
      header: 'Completed On',
      field: 'completedDate',
      emptyText: 'Not completed',
      visible: false,
    }),
    textColumn({
      header: 'Completed By',
      field: 'completedByUserId',
      visible: false,
      extra: {
        displayField: 'completedByUserName',
        sortField: 'completedByUserName',
        filter: {
          lookup: 'MemberView',
        },
      },
    }),
    dateColumn({
      header: 'Deadline',
      field: 'deadlineDate',
      dueIf: {
        field: 'completable',
        value: true,
      },
    }),
    textColumn({
      header: 'Schedule',
      field: 'scheduleDescription',
      allowFiltering: false,
      allowSorting: false,
      emptyText: '${if(smartTaskId)}Smart Task${else}One Off${/if}',
      width: 150,
    }),
    textColumn({
      header: 'Day',
      field: 'scheduleDaysDescription',
      allowFiltering: false,
      allowSorting: false,
      emptyText: 'Any',
      width: 100,
      visible: false,
    }),
    dateColumn({
      header: 'From',
      field: 'scheduleStartFromDate',
      visible: false,
    }),
    dateColumn({
      header: 'Until',
      field: 'scheduleContinueUntilDate',
      emptyText: '${if(recurringTaskId)}Ongoing${else}Not set${/if}',
      visible: false,
    }),
    dateColumn({
      header: 'Added',
      field: 'createdTime',
      visible: false,
    }),
    textColumn({
      header: 'Added By',
      field: 'createdByUserId',
      visible: false,
      extra: {
        displayField: 'createdByUserName',
        sortField: 'createdByUserName',
        filter: {
          lookup: 'MemberView',
        },
      },
    }),
    textColumn({
      header: 'Assigned To',
      field: 'assignedToUserId',
      width: 150,
      emptyText: 'Not assigned',
      extra: {
        displayField: 'assignedToUserName',
        sortField: 'assignedToUserName',
        filter: {
          lookup: 'MemberView',
        },
      },
    }),
    notesExcerptColumn({
      field: 'notesExcerpt',
    }),
    buttonColumn({
      uid: 'subTaskCompletedButton',
      header: 'Checklist Item Completed',
      field: 'currentSubTaskCompletedDate',
      type: 'date',
      action: 'setDateToToday',
      iconClass: 'fa-solid fa-check',
      class: 'btn-success',
      visible: false,
      toggleIf: {
        field: 'currentSubTaskCompletedDate',
        value: null,
        negate: true,
      },
      hideIf: {
        field: 'subTaskCount',
        value: null,
      },
    }),
    textColumn({
      header: 'Checklist',
      field: 'currentSubTask',
      template:
        '${if(subTaskCount && subTaskCount === subTaskCompleteCount)}<span class="text-success">Complete</span>${else}${if(currentSubTask)}${currentSubTask}<span class="float-end text-more-muted">(${currentSubTaskIndex}/${subTaskCount})</span>${else}<span class="text-more-muted">No checklist</span>${/if}${/if}',
      // '${if(subTaskCount)}${if(subTaskCount === subTaskCompleteCount)}<span class="text-success">Complete</span>${else}${currentSubTask}${/if}<span class="float-end text-more-muted">(${currentSubTaskIndex || subTaskCount}/${subTaskCount})</span>${else}<span class="text-more-muted">No checklist</span>${/if}',
      width: 200,
      visible: false,
      extra: {
        sortField: 'currentSubTaskIndex',
        filter: {
          lookup: true,
        },
      },
    }),
    textColumn({
      header: 'Status',
      field: 'status',
      textAlign: 'Center',
      template:
        '<span class="task-status task-status-${window.formatters.formatClassName(status)}">${window.formatters.formatTaskStatus(status)}</span>',
      extra: {
        filter: {
          options: enumOptions(TaskStatus),
        },
      },
    }),
    textColumn({
      header: 'Priority',
      field: 'priority',
      textAlign: 'Center',
      template: '<span class="priority priority-${priority}">${window.formatters.formatPriority(priority)}</span>',
      extra: {
        filter: {
          options: enumOptions(Priority),
        },
      },
    }),
  ],
};
